import React, { useEffect, useMemo, useState } from "react";
import { api, formatError } from "@/lib/api";
import { toast } from "sonner";
import { PageHeader } from "@/components/primitives";
import { Plus, PencilSimple, X, TreeStructure } from "@phosphor-icons/react";

const TYPE_COLOR = {
  asset:     "#0891b2",
  liability: "#be185d",
  equity:    "#4338ca",
  revenue:   "#059669",
  expense:   "#b45309",
};

const TYPE_LABELS = {
  asset: "Asset", liability: "Liability", equity: "Equity",
  revenue: "Revenue", expense: "Expense",
};

const BLANK = { code: "", name: "", type: "asset", parent_code: "", is_active: true };

export default function ChartOfAccounts() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);   // account being edited or {} for new
  const [form, setForm] = useState(BLANK);
  const [busy, setBusy] = useState(false);
  const [filter, setFilter] = useState("");

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get("/gl/accounts");
      setRows(data || []);
    } catch (e) { toast.error(formatError(e)); }
    setLoading(false);
  }
  useEffect(() => { load(); }, []);

  function openNew() { setForm({ ...BLANK }); setEditing({ new: true }); }
  function openEdit(a) {
    setForm({ code: a.code, name: a.name, type: a.type,
              parent_code: a.parent_code || "", is_active: a.is_active });
    setEditing(a);
  }

  async function save() {
    if (!form.code.trim() || !form.name.trim()) {
      toast.error("Code and name are required");
      return;
    }
    setBusy(true);
    try {
      if (editing?.new) {
        await api.post("/gl/accounts", form);
        toast.success(`Account ${form.code} created`);
      } else {
        await api.put(`/gl/accounts/${editing.id}`, form);
        toast.success(`Account ${form.code} updated`);
      }
      setEditing(null);
      await load();
    } catch (e) { toast.error(formatError(e)); }
    setBusy(false);
  }

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) =>
      r.code.toLowerCase().includes(q) ||
      r.name.toLowerCase().includes(q) ||
      r.type.toLowerCase().includes(q)
    );
  }, [rows, filter]);

  return (
    <div data-testid="coa-page">
      <PageHeader
        overline="Finance / GL"
        title="Chart of Accounts"
        subtitle="The single source of truth for how every transaction is classified."
        testId="coa-header"
      />

      <div className="phizo-card p-4 mb-4 flex items-center gap-3 flex-wrap">
        <TreeStructure size={16} weight="bold" className="text-[#71717a]" />
        <input
          type="text"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="Filter by code, name or type…"
          className="flex-1 min-w-[200px] border border-[#e4e4e7] bg-white px-3 py-1.5 text-xs outline-none focus:border-black"
          data-testid="coa-filter"
        />
        <span className="text-xs text-[#71717a] mono">{filtered.length} of {rows.length}</span>
        <button
          type="button"
          onClick={openNew}
          data-testid="coa-add-btn"
          className="bg-black text-white px-4 py-1.5 text-xs font-medium flex items-center gap-1.5 hover:bg-[#27272a]"
        >
          <Plus size={12} weight="bold" /> New account
        </button>
      </div>

      <div className="phizo-card overflow-x-auto" data-testid="coa-table">
        <table className="w-full text-xs">
          <thead className="bg-[#fafafa] border-b border-[#e4e4e7]">
            <tr className="text-left">
              <th className="px-3 py-2 phizo-label text-[9px]">Code</th>
              <th className="px-3 py-2 phizo-label text-[9px]">Name</th>
              <th className="px-3 py-2 phizo-label text-[9px]">Type</th>
              <th className="px-3 py-2 phizo-label text-[9px]">Parent</th>
              <th className="px-3 py-2 phizo-label text-[9px]">Status</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={6} className="p-6 text-center text-[#71717a]">Loading…</td></tr>}
            {!loading && filtered.map((a) => (
              <tr key={a.id} className="border-b border-[#f4f4f5] hover:bg-[#fafafa]"
                  data-testid={`coa-row-${a.code}`}>
                <td className="px-3 py-2 mono font-bold">{a.code}</td>
                <td className="px-3 py-2">
                  <div className="font-semibold">{a.name}</div>
                  {a.is_system && <div className="phizo-label text-[9px] text-[#a1a1aa]">Standard</div>}
                </td>
                <td className="px-3 py-2">
                  <span
                    className="mono text-[10px] px-1.5 py-0.5"
                    style={{ background: TYPE_COLOR[a.type] + "22", color: TYPE_COLOR[a.type] }}
                  >
                    {TYPE_LABELS[a.type]}
                  </span>
                </td>
                <td className="px-3 py-2 mono text-[11px] text-[#71717a]">{a.parent_code || "—"}</td>
                <td className="px-3 py-2">
                  {a.is_active
                    ? <span className="text-[10px] text-[#166534]">Active</span>
                    : <span className="text-[10px] text-[#991b1b]">Inactive</span>}
                </td>
                <td className="px-3 py-2 text-right">
                  <button
                    type="button"
                    onClick={() => openEdit(a)}
                    data-testid={`coa-edit-${a.code}`}
                    className="text-[#71717a] hover:text-black p-1"
                  >
                    <PencilSimple size={13} weight="bold" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
             onClick={() => setEditing(null)}>
          <div className="bg-white w-full max-w-md" onClick={(e) => e.stopPropagation()}
               data-testid="coa-modal">
            <div className="flex items-center justify-between px-5 py-3 border-b border-[#e4e4e7]">
              <div>
                <div className="phizo-label text-[10px] text-[#a1a1aa]">Chart of accounts</div>
                <h3 className="font-heading text-lg font-black tracking-tighter">
                  {editing.new ? "New account" : `Edit ${editing.code}`}
                </h3>
              </div>
              <button onClick={() => setEditing(null)} className="p-1 text-[#71717a] hover:text-black">
                <X size={16} weight="bold" />
              </button>
            </div>
            <div className="p-5 space-y-3">
              <label className="block text-xs">
                <div className="phizo-label text-[10px] mb-1">Code</div>
                <input
                  type="text"
                  value={form.code}
                  onChange={(e) => setForm({ ...form, code: e.target.value })}
                  disabled={editing.is_system}
                  className="w-full border border-[#e4e4e7] px-3 py-1.5 mono text-sm outline-none focus:border-black disabled:bg-[#f4f4f5]"
                  data-testid="coa-form-code"
                />
                {editing.is_system && (
                  <div className="text-[10px] text-[#71717a] mt-1">Standard accounts cannot be renumbered.</div>
                )}
              </label>
              <label className="block text-xs">
                <div className="phizo-label text-[10px] mb-1">Name</div>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="w-full border border-[#e4e4e7] px-3 py-1.5 text-sm outline-none focus:border-black"
                  data-testid="coa-form-name"
                />
              </label>
              <label className="block text-xs">
                <div className="phizo-label text-[10px] mb-1">Type</div>
                <select
                  value={form.type}
                  onChange={(e) => setForm({ ...form, type: e.target.value })}
                  disabled={editing.is_system}
                  className="w-full border border-[#e4e4e7] px-3 py-1.5 text-sm outline-none focus:border-black disabled:bg-[#f4f4f5]"
                  data-testid="coa-form-type"
                >
                  {Object.entries(TYPE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
              </label>
              <label className="block text-xs">
                <div className="phizo-label text-[10px] mb-1">Parent code (optional)</div>
                <input
                  type="text"
                  value={form.parent_code}
                  onChange={(e) => setForm({ ...form, parent_code: e.target.value })}
                  placeholder="e.g. 1100"
                  className="w-full border border-[#e4e4e7] px-3 py-1.5 mono text-sm outline-none focus:border-black"
                  data-testid="coa-form-parent"
                />
              </label>
              <label className="flex items-center gap-2 text-xs">
                <input
                  type="checkbox"
                  checked={form.is_active}
                  onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
                />
                Active
              </label>
            </div>
            <div className="px-5 py-3 border-t border-[#e4e4e7] flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setEditing(null)}
                className="border border-[#e4e4e7] px-4 py-1.5 text-xs hover:bg-[#f4f4f5]"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={save}
                disabled={busy}
                data-testid="coa-save-btn"
                className="bg-[#002FA7] text-white px-4 py-1.5 text-xs font-medium hover:bg-[#001f7a] disabled:opacity-60"
              >
                {busy ? "Saving…" : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
